// src/components/FirebaseErrorBoundary.jsx - Enhanced Version
import React from 'react';
import { categorizeFirebaseError } from '../services/firebase';
import { logger } from "../utils/logger";

class FirebaseErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      retryCount: 0,
      isRetrying: false,
      errorType: null,
      lastErrorTime: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    // Categorize the error type for better handling
    const errorType = categorizeFirebaseError(error);
    
    return { 
      hasError: true, 
      error, 
      errorType,
      lastErrorTime: new Date().toISOString()
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔥 Firebase Error Boundary caught error:', error, errorInfo);
    
    // Enhanced error logging with categorization
    const errorData = {
      message: error.message,
      stack: error.stack,
      code: error.code,
      errorType: this.state.errorType,
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // Log to console with detailed info
    console.group('🔥 Firebase Error Details');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Categorized as:', this.state.errorType);
    console.error('Props:', this.props);
    console.groupEnd();
    
    // Log to analytics service (Google Analytics)
    if (window.gtag) {
      window.gtag('event', 'firebase_error', {
        error_type: this.state.errorType,
        error_message: error.message,
        retry_count: this.state.retryCount,
        custom_parameters: {
          error_code: error.code,
          component_stack: errorInfo.componentStack?.slice(0, 500) // Truncate for GA
        }
      });
    }
    
    // Report to error tracking service (Sentry)
    if (window.Sentry) {
      window.Sentry.withScope((scope) => {
        scope.setTag('component', 'FirebaseErrorBoundary');
        scope.setTag('errorType', this.state.errorType);
        scope.setLevel('error');
        scope.setContext('errorBoundary', {
          componentStack: errorInfo.componentStack,
          retryCount: this.state.retryCount,
          props: this.props
        });
        window.Sentry.captureException(error);
      });
    }
    
    // Custom error reporting hook
    if (this.props.onError && typeof this.props.onError === 'function') {
      this.props.onError(errorData);
    }
  }

  handleRetry = async () => {
    logger.component('🔄 Retrying Firebase operation...');
    this.setState({ isRetrying: true });
    
    // Add a delay for better UX and to avoid immediate re-errors
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if we should attempt to restore Firebase connection
    await this.attemptConnectionRestore();
    
    this.setState(prev => ({ 
      hasError: false, 
      error: null, 
      retryCount: prev.retryCount + 1,
      isRetrying: false,
      errorType: null
    }));
  };

  attemptConnectionRestore = async () => {
    try {
      // Try to re-enable Firebase network if it was disabled
      if (typeof window !== 'undefined' && window.firebase && window.db) {
        logger.network('🌐 Attempting to restore Firebase connection...');
        
        // Try to enable network
        await window.db.enableNetwork();
        
        // Simple connectivity test
        const testRef = window.db.collection('health').doc('test');
        await testRef.get();
        
        logger.success('✅ Firebase connection restored');
      }
    } catch (restoreError) {
      console.warn('⚠️ Failed to restore Firebase connection:', restoreError);
    }
  };

  handleRefresh = () => {
    logger.component('🔄 Refreshing page...');
    window.location.reload();
  };

  handleContinueOffline = () => {
    console.log('📱 Continuing in offline mode...');
    this.setState({ 
      hasError: false, 
      error: null,
      retryCount: 0 
    });
    
    // Disable Firebase network to prevent further errors
    if (typeof window !== 'undefined' && window.firebase && window.db) {
      window.db.disableNetwork().catch(err => 
        console.warn('Failed to disable network:', err)
      );
    }
  };

  getErrorMessage = () => {
    const { error, errorType } = this.state;
    
    switch (errorType) {
      case 'auth':
        return 'Authentication issue. You may need to sign in again.';
      case 'firestore':
        return 'Database connection issue. Your data might be temporarily unavailable.';
      case 'storage':
        return 'File upload/download issue. Please try again.';
      case 'network':
        return 'Network connection issue. Please check your internet connection.';
      case 'timeout':
        return 'Request timed out. Please try again.';
      case 'permission':
        return 'Permission denied. You may not have access to this data.';
      case 'sportsdata':
        return 'Sports data service is temporarily unavailable.';
      default:
        return error?.message || 'An unexpected error occurred. Please try again.';
    }
  };

  getErrorIcon = () => {
    const { errorType } = this.state;
    
    switch (errorType) {
      case 'auth': return '🔐';
      case 'firestore': return '💾';
      case 'storage': return '📁';
      case 'network': return '🌐';
      case 'timeout': return '⏱️';
      case 'permission': return '🚫';
      case 'sportsdata': return '🏈';
      default: return '🔥';
    }
  };

  getActionButtons = () => {
    const { retryCount, isRetrying, errorType } = this.state;
    const maxRetries = 3;
    const buttons = [];

    // Retry button (if not exceeded max retries)
    if (retryCount <= maxRetries) {
      buttons.push(
        <button 
          key="retry"
          onClick={this.handleRetry}
          disabled={isRetrying}
          style={{
            backgroundColor: isRetrying ? '#9ca3af' : '#dc2626',
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '6px',
            cursor: isRetrying ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            marginRight: '0.5rem',
            transition: 'all 0.2s ease'
          }}
        >
          {isRetrying ? 'Retrying...' : 'Try Again'}
        </button>
      );
    }

    // Refresh button (for persistent errors)
    if (retryCount > 1) {
      buttons.push(
        <button 
          key="refresh"
          onClick={this.handleRefresh}
          style={{
            backgroundColor: '#059669',
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem',
            marginRight: '0.5rem'
          }}
        >
          Refresh Page
        </button>
      );
    }

    // Continue offline (for network errors)
    if (errorType === 'network' || errorType === 'timeout' || retryCount > maxRetries) {
      buttons.push(
        <button 
          key="offline"
          onClick={this.handleContinueOffline}
          style={{
            backgroundColor: '#6b7280',
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Continue Offline
        </button>
      );
    }

    return buttons;
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { retryCount, error, errorType, lastErrorTime, showDetails } = this.state;
    const actionButtons = this.getActionButtons();

    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        margin: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        {/* Error Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ 
            margin: '0 0 1rem 0', 
            fontSize: '1.5rem',
            color: '#dc2626'
          }}>
            {this.getErrorIcon()} Connection Issue
          </h2>
          <p style={{ 
            marginBottom: '0.5rem', 
            color: '#374151',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            {this.getErrorMessage()}
          </p>
          {retryCount > 0 && (
            <p style={{ 
              fontSize: '0.875rem', 
              color: '#6b7280',
              margin: '0.5rem 0'
            }}>
              Attempt {retryCount + 1} of {3 + 1}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ marginBottom: '1.5rem' }}>
          {actionButtons}
        </div>

        {/* Additional Help Text */}
        {retryCount > 2 && (
          <div style={{ 
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151' }}>
              Still having trouble?
            </h4>
            <ul style={{ 
              textAlign: 'left', 
              margin: '0',
              paddingLeft: '1.5rem',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}>
              <li>Check your internet connection</li>
              <li>Try refreshing the page</li>
              <li>Clear your browser cache</li>
              <li>Try again in a few minutes</li>
            </ul>
          </div>
        )}

        {/* Technical Details Toggle */}
        {(this.props.showErrorDetails || process.env.NODE_ENV === 'development') && (
          <div style={{ marginTop: '1rem' }}>
            <button
              onClick={this.toggleDetails}
              style={{
                background: 'none',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: '#6b7280'
              }}
            >
              {showDetails ? 'Hide' : 'Show'} Technical Details
            </button>
            
            {showDetails && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                textAlign: 'left'
              }}>
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Error Type:</strong> {errorType || 'unknown'}
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Time:</strong> {lastErrorTime}
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Retry Count:</strong> {retryCount}
                </div>
                {error?.code && (
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Error Code:</strong> {error.code}
                  </div>
                )}
                <div>
                  <strong>Error Message:</strong>
                  <pre style={{
                    backgroundColor: '#ffffff',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    margin: '0.5rem 0 0 0',
                    border: '1px solid #d1d5db'
                  }}>
                    {error?.message || 'No error message available'}
                  </pre>
                </div>
                {error?.stack && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong>Stack Trace:</strong>
                    <pre style={{
                      backgroundColor: '#ffffff',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      margin: '0.5rem 0 0 0',
                      border: '1px solid #d1d5db',
                      maxHeight: '200px'
                    }}>
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Connection Status */}
        {this.props.connectionStatus && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}>
            <strong>Connection Status:</strong> {this.props.connectionStatus}
          </div>
        )}
      </div>
    );
  }
}

// Higher-order component for wrapping individual Firebase operations
export const withFirebaseErrorHandling = (WrappedComponent) => {
  return class FirebaseErrorWrapper extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
      const errorType = categorizeFirebaseError(error);
      return { hasError: true, error, errorType };
    }

    componentDidCatch(error, errorInfo) {
      console.error('Firebase operation error:', error, errorInfo);
      
      // Report to analytics
      if (window.gtag) {
        window.gtag('event', 'firebase_operation_error', {
          error_type: this.state.errorType,
          component_name: WrappedComponent.name || 'Unknown',
          error_message: error.message
        });
      }
    }

    handleRetry = () => {
      this.setState({ hasError: false, error: null });
    };

    render() {
      if (this.state.hasError) {
        return (
          <div style={{
            padding: '1rem',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '4px',
            margin: '0.5rem 0'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', color: '#dc2626' }}>
              ⚠️ Operation failed: {this.state.error?.message}
            </p>
            <button
              onClick={this.handleRetry}
              style={{
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                padding: '0.25rem 0.75rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Try Again
            </button>
          </div>
        );
      }

      return <WrappedComponent {...this.props} />;
    }
  };
};

// Hook for handling Firebase errors in functional components
export const useFirebaseErrorHandler = () => {
  const [error, setError] = React.useState(null);
  const [isRetrying, setIsRetrying] = React.useState(false);

  const handleError = React.useCallback((error) => {
    console.error('Firebase operation failed:', error);
    const errorType = categorizeFirebaseError(error);
    
    setError({
      ...error,
      errorType,
      timestamp: new Date().toISOString()
    });
    
    // Auto-retry for certain error types
    if (errorType === 'network' || errorType === 'timeout') {
      setIsRetrying(true);
      setTimeout(() => {
        setIsRetrying(false);
        setError(null);
      }, 3000);
    }

    // Report to analytics
    if (window.gtag) {
      window.gtag('event', 'firebase_hook_error', {
        error_type: errorType,
        error_message: error.message
      });
    }
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
    setIsRetrying(false);
  }, []);

  const retry = React.useCallback(() => {
    setError(null);
    setIsRetrying(false);
  }, []);

  return {
    error,
    isRetrying,
    handleError,
    clearError,
    retry,
    hasError: Boolean(error)
  };
};

export default FirebaseErrorBoundary;